import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import _, { debounce } from "lodash";

import { setDisableBtn, setIsLoading, setIsPrayerCampaign, toggleDialog } from "../redux/Slices/Global/globalSlice";
import { useDataTableContext } from "../pages/Contexts/DataTableContext";
import { setCompanyCampaigns } from "../redux/Slices/deviceSlice";
import { handleResErrors } from "./utils";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import axiosInstance from "../config/Axios";
import { setIsPublished } from "../redux/Slices/layoutSlice";
import { useEffect } from "react";

//--------------------------Get Company Campaigns ( published and based n subscription)--------------------------
const getCampaigns = async ({ pageParam = 1, queryKey }) => {
    const [_key, { per_page, search }] = queryKey;

    const { data } = await axiosInstance.get(`/company/campaigns`, {
        params: {
            page: pageParam,
            per_page,
            search
        }
    });

    return {
        items: data.data,
        currentPage: data.pagination.current_page,
        totalPages: data.pagination.total_pages
    };
};


export const useGetCampaigns = ({ per_page = 10, search = "" }, options = {}) => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch();

    return useInfiniteQuery(
        ['getCampaigns', { per_page, search }],
        getCampaigns,
        {
            getNextPageParam: (lastPage) => {
                if (lastPage.currentPage < lastPage.totalPages) {
                    return lastPage.currentPage + 1;
                }
                return undefined;
            },
            keepPreviousData: true,  // keeps old data while fetching new
            staleTime: 1000 * 60 * 5,// 5 min cache
            onMutate: () => {
                dispatch(setIsLoading(true));
            },
            onSuccess: (data) => {
                // flatten items across pages
                const allItems = data.pages.flatMap((page) => page.items);
                dispatch(setCompanyCampaigns(allItems));
                dispatch(setIsLoading(false));
            },
            onError: (error) => {
                showToast(
                    "error",
                    "Fetching Data",
                    error.response?.data?.message || "Unexpected error"
                );
                dispatch(setIsLoading(false));
            },
            ...options,
        }
    );

}

//--------------------------Get Campaigns--------------------------
const getAllCampaigns = async ({ pageParam = 1, queryKey }) => {
    const [key, { per_page, search }] = queryKey;
    const isInteractive = key.includes("interactive");

    const { data } = await axiosInstance.get(`/campaign/all${isInteractive ? "/interactive" : ""}`, {
        params: { page: pageParam, per_page, search }
    });

    return {
        items: data.data,
        currentPage: data.pagination.current_page,
        totalItems: data.pagination.total,
        totalPages: data.pagination.total_pages
    };
};

export const useGetAllCampaigns = ({ per_page = 10, search = "" }) => {
    const dispatch = useDispatch();
    const { showToast } = useCustomToast();
    const interactive = window.location.pathname.includes("interactive")
        ? "interactive"
        : "normal";


    return useInfiniteQuery(
        [`allCampaigns-${interactive}`, { per_page, search }],
        getAllCampaigns,
        {
            getNextPageParam: (lastPage) => {
                if (lastPage.currentPage < lastPage.totalPages) {
                    return lastPage.currentPage + 1;
                }
                return undefined;
            },
            staleTime: 1000 * 60 * 5,
            keepPreviousData: true,

            onMutate: () => {
                dispatch(setIsLoading(true));
            },
            onSuccess: (data) => {
                // 
            },
            onError: (error) => {
                //  
            }
        }
    );
};

//--------------------------Create Campaign--------------------------
const createCampaign = async (payload) => {
    const { data } = await axiosInstance.post("/campaign/create", payload);

    return data.data;
}

export const useCreateCampaignMutation = () => {
    const { isInteractiveCampaign } = useDataTableContext();
    const { showToast } = useCustomToast();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();
    const interactive = window.location.pathname.includes("interactive")
        ? "interactive"
        : "normal";


    return useMutation(createCampaign, {
        onSuccess: async (data) => {
            dispatch(setDisableBtn(false))
            dispatch(setIsPrayerCampaign(false))
            dispatch(toggleDialog("createCampaign"));
            showToast('success', 'Campaign Creation', "Campaign created successfully!");
            queryClient.invalidateQueries({ queryKey: [`allCampaigns-${interactive}`] });
            queryClient.invalidateQueries({ queryKey: [`getCampaigns`] });

            sessionStorage.setItem('is_default_prayer', JSON.stringify(false));

            if (interactive === "interactive")
                navigate(`/interactive/campaign/${data?.id}/setup`)
            else
                navigate(`/campaigns/${data?.id}/setup`)
        },
        onError: (error) => {
            dispatch(setDisableBtn(false));
            handleResErrors(error, showToast)
        }
    })
}

//--------------------------get Campaign usage--------------------------
const campaignUsage = async (payload) => {
    const { data } = await axiosInstance.get(`/campaign/${payload.id}/usage`);

    return data.data;
}

export const useCampaignUsage = () => {
    const { showToast } = useCustomToast();


    return useMutation(campaignUsage, {
        onSuccess: async (data) => {

            //   
        },
        onError: (error) => {
            // dispatch(setDisableBtn(false));
            handleResErrors(error, showToast)
        }
    })
}

//--------------------------Update Campaign--------------------------
const deleteCampaign = async (payload) => {
    const { data } = await axiosInstance.delete(`/campaign/${payload.id}`);
    return data.data;
}

export const useDeleteCampaignMutation = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();
    const interactive = window.location.pathname.includes("interactive")
        ? "interactive"
        : "normal";

    return useMutation(deleteCampaign, {
        onSuccess: (updatedData) => {
            showToast('success', 'Campaign Deleted', "Campaign Deleted successfully!");
            queryClient.invalidateQueries({ queryKey: [`allCampaigns-${interactive}`] });
            queryClient.invalidateQueries({ queryKey: [`getCampaigns`] });

        },
        onError: (error) => handleResErrors(error, showToast)

    })
}


//--------------------------Update Campaign--------------------------
const updateCampaign = async (payload) => {
    const { data } = await axiosInstance.put(`/campaign/${payload.id}/update`, payload.data);
    return data.data;
}

export const useUpdateCampaignMutation = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();

    return useMutation(updateCampaign, {
        onSuccess: (updatedData) => {
            if (updatedData.status === "DRAFT") {
                dispatch(setIsPublished(false));
            }
            queryClient.invalidateQueries(["layoutData"]);
            queryClient.invalidateQueries(["screensData"]);
        },
        onError: (error) => handleResErrors(error, showToast)

    })
}



/* ------------------ Live Media Animation Settings ------------------ */
const updateLiveAnimationSettings = async (payload) => {
    const { data } = await axiosInstance.patch(
        `/campaign/${payload.id}/update/animation`,
        payload.data
    );
    return data.data;
};

export const useUpdateLiveAnimationSettingsMutation = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { campaignDetails, isLoading } = useSelector((s) => s.global) ?? {};


    return useMutation(updateLiveAnimationSettings, {
        onMutate: () => {
            dispatch(setIsLoading(true))

        },
        onSuccess: () => {
            dispatch(toggleDialog('layerSettings'))
            queryClient.invalidateQueries(['getCampaign', campaignDetails.id])

        },
        onError: (err) => handleResErrors(err, showToast),
    });
};


//--------------------------Publish Campaign--------------------------
const publishCampaign = async (payload) => {
    const { data } = await axiosInstance.post(`/campaign/${payload.id}/publish`, {});
    return data.data;
}

export const usePublishCampaignMutation = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const interactive = window.location.pathname.includes("interactive")
        ? "interactive"
        : "normal";
    return useMutation(publishCampaign, {
        onSuccess: async (res, variables) => {
            if (window.location.pathname.includes("campaign")) {
                queryClient.invalidateQueries(['getCampaign', variables.id])
                queryClient.invalidateQueries({ queryKey: [`allCampaigns-${interactive}`] });
                queryClient.invalidateQueries({ queryKey: [`getCampaigns`] });

            }

        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//--------------------------Reset Campaign--------------------------
const resetCampaign = async (payload) => {
    const { data } = await axiosInstance.post(`/campaign/${payload.id}/reset`, {});

    return data.data;
}

export const useResetCampaignMutation = () => {
    const { showToast } = useCustomToast();
    const updateCampaign = useUpdateCampaignMutation();
    const queryClient = useQueryClient();
    const { campaignDetails, layer } = useSelector((state) => state.global);
    const screenDetails = useSelector((state) => state.layout.screenDetails);

    let campaignId = window.location.pathname.includes("layout") ? screenDetails?.id : campaignDetails.id
    let campaignLayer = window.location.pathname.includes("layout") ? "MAIN" : layer

    return useMutation(resetCampaign, {
        onSuccess: async (res, variables) => {
            await updateCampaign.mutateAsync({
                id: variables.id,
                data: {
                    status: "PUBLISHED",
                    name: variables?.name,
                },
            }).then(() => {
                if (window.location.pathname.includes("layout")) {
                    queryClient.invalidateQueries(["screensData"], screenDetails?.pivot.layout_id)
                } else {
                    queryClient.invalidateQueries(['getCampaign', variables.id])
                }
                // queryClient.invalidateQueries(['getCampaignLayerContent', campaignId, campaignLayer])
                queryClient.invalidateQueries({
                    queryKey: ['getCampaignLayerContent', campaignId],
                    exact: false, // allow partial match
                });

            }

            )
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//--------------------------Get Campaign By ID--------------------------
const getCampaign = async (id) => {
    const { data } = await axiosInstance.get(`/campaign/${id}`);
    return data.data;
}

export const useGetCampaign = (param) => {
    const { showToast } = useCustomToast();
    const { id, enabled } = param
    const dispatch = useDispatch()

    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getCampaign', id],
        () => getCampaign(id),
        {
            enabled,    // Control whether the query runs automatically
            keepPreviousData: true,  // keeps old data while fetching new
            staleTime: 1000 * 60 * 5,// 5 min cache
            onSuccess: async () => {
                dispatch(setIsLoading(false))
            },
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}

//--------------------------Get Campaign Layer Mixedness--------------------------
// Whether each layer can safely collapse to one swappable slot — see
// App\Support\CampaignLayerMixedness on the backend. Used by the Show Page.
const getCampaignLayerMixedness = async (id) => {
    const { data } = await axiosInstance.get(`/campaign/${id}/layer-mixedness`);
    return data.data;
}

export const useGetCampaignLayerMixedness = (id, enabled = true) => {
    const { showToast } = useCustomToast();
    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getCampaignLayerMixedness', id],
        () => getCampaignLayerMixedness(id),
        {
            enabled: enabled && !!id,
            staleTime: 1000 * 60 * 5,
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}

//--------------------------Get Campaign Content by layer--------------------------
const getCampaignLayerContent = async ({ queryKey }) => {
    const [, id, layer] = queryKey
    const { data } = await axiosInstance.get(`/campaign/content/${id}/${layer}`);
    return data.data;

}

export const useGetCampaignLayerContent = (id, layer) => {
    const { showToast } = useCustomToast();
    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getCampaignLayerContent', id, layer],
        getCampaignLayerContent,
        {
            enabled: !!id,
            keepPreviousData: false,  // Don't keep old data to show fresh updates immediately
            staleTime: 0,  // Always refetch when invalidated for real-time updates
            cacheTime: 1000 * 60 * 2,  // Keep in cache for 2 minutes
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}


//--------------------------Get Campaign Content--------------------------
const getCampaignContent = async ({ queryKey }) => {
    const [, campaignId] = queryKey
    const { data } = await axiosInstance.get(`/campaign/media/${campaignId}`);
    return data;

}

export const useGetCampaignContent = (campaignId) => {
    const { showToast } = useCustomToast();
    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getCampaignContent', campaignId],
        getCampaignContent,
        {
            enabled: !!campaignId,
            cacheTime: 0,
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}


//--------------------------Get Campaign Content for preview--------------------------
const previewCampaignContent = async ({ queryKey }) => {
    const [, campaignId] = queryKey
    const { data } = await axiosInstance.get(`/campaign/media/preview/${campaignId}`);
    return data.data;

}

export const usePreviewCampaignContent = (campaignId) => {
    const { showToast } = useCustomToast();
    const { isLoading, data, error, isError, refetch } = useQuery(
        ['previewCampaignContent', campaignId],
        previewCampaignContent,
        {
            enabled: !!campaignId,
            cacheTime: 0,
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}




//--------------------------Get Campaign Content for preview--------------------------
const previewCampaign = async ({ queryKey }) => {
    const [, campaignId] = queryKey
    const { data } = await axiosInstance.get(`/campaign/preview/${campaignId}`);
    return data.data;

}

export const usePreviewCampaign = (campaignId) => {
    const { showToast } = useCustomToast();
    const { isLoading, data, error, isError, refetch } = useQuery(
        ['previewCampaign', campaignId],
        previewCampaign,
        {
            enabled: !!campaignId,
            cacheTime: 0,
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, error, isError, refetch };
}